import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Lock, Save, Loader2, PieChart as PieIcon } from "lucide-react";
import { C, Panel, PieCard, BarCard, StatusBadge, fmtMoney } from "./reportsUi";

export default function MoneyFlowTab({ data }) {
  const [cfg, setCfg] = useState(null);
  const [roles, setRoles] = useState({});
  const [notes, setNotes] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [splitRoles, setSplitRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadCfg = async () => {
    try {
      const { data: d } = await api.get("/reports/center/money-flow/splits");
      setCfg(d.splits); setCanEdit(d.can_edit); setSplitRoles(d.split_roles || []);
      const r = {};
      (d.split_roles || []).forEach(({ key }) => { r[key] = { percent: d.splits?.roles?.[key]?.percent ?? "", note: d.splits?.roles?.[key]?.note ?? "" }; });
      setRoles(r); setNotes(d.splits?.notes || "");
    } catch { /* non-blocking */ }
  };
  useEffect(() => { loadCfg(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/reports/center/money-flow/splits", { roles, notes });
      setCfg(d.splits); toast.success("Split placeholders saved (display-only).");
    } catch (e) { toast.error(errMsg(e, "Could not save splits.")); }
    finally { setSaving(false); }
  };

  const byStatus = data.by_status || [];

  return (
    <div className="space-y-4" data-testid="rc-moneyflow">
      <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-[12.5px]" style={{ backgroundColor: "rgba(156,30,34,0.08)", color: C.red }}>
        <Lock size={15} className="mt-0.5 shrink-0" />
        <span>{data.note}</span>
      </div>

      {/* Status breakdown */}
      <Panel title="Money flow by status" testid="mf-status">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
          {byStatus.map((s) => (
            <div key={s.name} data-testid={`mf-status-${s.name.replace(/\W+/g, "-").toLowerCase()}`} className="rounded-[0.8rem] border p-3" style={{ borderColor: C.line }}>
              <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.slate }}>{s.name}</div>
              {s.not_tracked ? (
                <div className="mt-1.5"><StatusBadge label="Not tracked yet" tone={C.slate} /></div>
              ) : (
                <div className="mt-1 font-display font-bold text-lg tabular-nums" style={{ color: C.navy }}>{fmtMoney(s.value)}</div>
              )}
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PieCard title="Revenue by status" data={byStatus.filter((s) => !s.not_tracked).map((s) => ({ name: s.name, value: s.value }))} money testid="mf-pie-status" />
        <PieCard title="Revenue by service lane" data={data.charts?.by_service_lane} money testid="mf-pie-lane" />
        <BarCard title="Revenue by territory" data={data.charts?.by_territory} money color={C.navy} testid="mf-bar-terr" />
        <BarCard title="Revenue by franchise" data={data.charts?.by_franchise} money color={C.green} testid="mf-bar-fr" />
        <BarCard title="Revenue by opened-by" data={data.charts?.by_opened_by} money color={C.navy} testid="mf-bar-opened" />
        <BarCard title="Revenue by managed-by" data={data.charts?.by_managed_by} money color={C.sky} testid="mf-bar-managed" />
        <BarCard title="Revenue by closed-by" data={data.charts?.by_closed_by} money color={C.green} testid="mf-bar-closed" />
        <BarCard title="Revenue by partner / facility" data={data.charts?.by_partner} money color={C.amber} testid="mf-bar-partner" />
      </div>

      {/* Split placeholder config */}
      <Panel title="Money-flow split placeholders" testid="mf-splits"
        subtitle="Reference-only percentages by role. NEVER used to compute real payouts."
        right={canEdit ? (
          <Button data-testid="mf-splits-save" size="sm" disabled={saving} onClick={save} className="h-9 gap-1 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
          </Button>
        ) : <StatusBadge label="Super Admin only" tone={C.slate} />}>
        {!canEdit && !cfg && (
          <div className="py-6 text-center text-[13px]" style={{ color: C.slate }} data-testid="mf-splits-empty"><PieIcon size={22} className="mx-auto mb-1.5" style={{ color: C.sky }} /> Split not configured yet.</div>
        )}
        {(canEdit || cfg) && (
          <div className="space-y-2.5">
            {splitRoles.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3 flex-wrap" data-testid={`mf-split-${key}`}>
                <div className="w-40 text-[13px] font-medium" style={{ color: C.navy }}>{label}</div>
                {canEdit ? (
                  <>
                    <div className="flex items-center gap-1">
                      <Input type="number" data-testid={`mf-split-pct-${key}`} value={roles[key]?.percent ?? ""} onChange={(e) => setRoles((p) => ({ ...p, [key]: { ...p[key], percent: e.target.value } }))} placeholder="—" className="h-9 w-24 text-[13px]" />
                      <span className="text-[13px]" style={{ color: C.slate }}>%</span>
                    </div>
                    <Input data-testid={`mf-split-note-${key}`} value={roles[key]?.note ?? ""} onChange={(e) => setRoles((p) => ({ ...p, [key]: { ...p[key], note: e.target.value } }))} placeholder="Note (optional)" className="h-9 flex-1 min-w-[160px] text-[13px]" />
                  </>
                ) : (
                  <div className="text-[13px]" style={{ color: C.charcoal }}>
                    {cfg?.roles?.[key]?.percent != null ? <b>{cfg.roles[key].percent}%</b> : <span style={{ color: C.slate }}>Split not configured yet.</span>}
                    {cfg?.roles?.[key]?.note && <span style={{ color: C.slate }}> · {cfg.roles[key].note}</span>}
                  </div>
                )}
              </div>
            ))}
            {canEdit && (
              <div className="pt-1">
                <Textarea data-testid="mf-split-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="General notes about these placeholders…" />
              </div>
            )}
            <p className="text-[11.5px] pt-1" style={{ color: C.slate }}>These placeholders are for reference only and are not applied to any revenue, payout, or commission calculation.</p>
          </div>
        )}
      </Panel>
    </div>
  );
}
